<template>
  <div class="relative text-base">
    <div
      class="-mx-2 space-y-3 border border-black px-5 py-8 md:border-black-60 md:p-5 lg:mx-0 lg:min-w-[20.375rem]"
      :class="{ 'sticky top-32': isBuyBoxSticky, 'border-none': borderless }"
    >
      <div class="border-b border-black-20 pb-3 md:border-black-60">
        <Headline tag="div" size="xl" :is-uppercase="false">
          {{ $t('basket.total') }}
        </Headline>
      </div>
      <dl
        class="text-gray-800 grid grid-cols-2 justify-between gap-y-2.5 border-b border-black-20 pb-3 text-base md:border-black-60"
      >
        <dt v-if="hasDiscounts">{{ $t('basket.subtotal') }}</dt>
        <dd
          v-if="hasDiscounts"
          class="text-gray-500 text-right line-through"
          data-qa="cart-original-total"
        >
          {{ formattedOriginalTotal }}
        </dd>
        <dt v-if="hasDiscounts" class="text-red">
          {{ $t('basket.discount') }}
        </dt>
        <dd
          v-if="hasDiscounts"
          class="text-right text-red"
          data-qa="cart-discount"
        >
          {{ formattedDiscount }}
        </dd>
        <dt v-if="!hasDiscounts && subTotal">
          {{ $t('basket.subtotal') }}
        </dt>
        <dd
          v-if="!hasDiscounts && subTotal"
          class="text-right"
          data-qa="cart-subtotal"
        >
          {{ subTotal }}
        </dd>
        <dt v-if="formattedShippingPrice">{{ $t('basket.shipping') }}</dt>
        <dd
          v-if="formattedShippingPrice && !isUserPremium"
          class="text-right"
          data-qa="cart-shipping-cost"
        >
          {{ formattedShippingPrice }}
        </dd>
        <dd
          v-if="isUserPremium"
          class="text-right"
          data-qa="cart-shipping-cost"
        >
          {{ $t('basket.free_shipping') }}
        </dd>
      </dl>
      <div
        v-if="totalPrice"
        class="text-gray-800 flex justify-between pb-3 text-lg font-bold md:hidden"
      >
        <div>
          {{ $t('basket.you_pay') }}
          <span class="text-gray-500 text-sm font-normal leading-3">{{
            $t('basket.including_vat')
          }}</span>
        </div>
        <div class="text-right font-bold" data-qa="cart-grand-total">
          {{ totalPrice }}
        </div>
      </div>
      <dl
        v-if="totalPrice"
        class="text-gray-800 hidden grid-cols-2 justify-between text-lg font-bold md:grid lg:text-base xl:text-lg"
      >
        <dt class="flex flex-wrap items-baseline gap-2 3xl:flex-nowrap">
          <span class="whitespace-nowrap">{{ $t('basket.you_pay') }}</span>
          <span
            class="text-gray-500 whitespace-nowrap text-sm font-normal leading-3"
          >
            {{ $t('basket.including_vat') }}
          </span>
        </dt>
        <dd class="text-right" data-qa="cart-grand-total">
          {{ totalPrice }}
        </dd>
      </dl>
      <template v-if="!hideCheckoutSection">
        <Button
          id="checkout-button"
          class="!mt-6 uppercase shadow-button"
          data-test-id="checkout-link"
          is-full-width
          :disabled="isCheckoutButtonDisabled"
          ignore-capitalisation
          data-qa="go-to-checkout"
          @click="onClickToCheckoutOrder"
        >
          {{ $t('basket.to_checkout') }}
        </Button>
        <p class="text-center text-sm">
          {{ $t('basket.coupon_message') }}
        </p>
        <PaymentMethods
          v-if="paymentMethods?.length > 0"
          :payment-methods="paymentMethods"
        />
      </template>
    </div>

    <div
      v-if="!hideCheckoutSection"
      class="fixed inset-x-0 bottom-0 border-t border-black-10 bg-white px-4 py-2 transition-all duration-200 md:hidden"
      :class="{
        'pointer-events-none translate-y-full opacity-0':
          !showStickyCheckoutButton,
      }"
    >
      <Button
        class="!mt-0 uppercase shadow-button"
        data-test-id="checkout-link"
        :disabled="isCheckoutButtonDisabled"
        is-full-width
        ignore-capitalisation
        data-qa="go-to-checkout"
        @click="onClickToCheckoutOrder"
      >
        {{ $t('basket.to_checkout') }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue'
import useCms from '~/composables/cms/useCms'
import type { SbPaymentMethods } from '~/cms/storyblok/types/storyblok.gen'
import useUserPremiumCheck from '#layers/base/composables/useUserPremiumCheck'
import useBasketSummary from '~/composables/useBasketSummary'

defineProps<{
  showStickyCheckoutButton?: boolean
  isCheckoutButtonDisabled?: boolean
  borderless?: boolean
  hideCheckoutSection?: boolean
}>()

const {
  onClickToCheckoutOrder,
  hasDiscounts,
  isBuyBoxSticky,
  formattedOriginalTotal,
  formattedDiscount,
  formattedShippingPrice,
  totalPrice,
  subTotal,
} = useBasketSummary()

const { isUserPremium } = useUserPremiumCheck()

const { fetchBySlug: fetchPaymentMethods, data: paymentMethodsData } =
  useCms<SbPaymentMethods>('payment-methods')

const paymentMethods = computed(() => {
  return (
    paymentMethodsData.value?.content?.methods?.filter(
      ({ visible }) => visible,
    ) ?? []
  )
})

onMounted(async () => {
  await fetchPaymentMethods('payment-methods')
})
</script>
